import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Mail, Send, Search, Info, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import { emailApi } from '../../services/emailApi';
import type {
    SuggestRecipientsResponse,
    SuggestedEmployee,
    SuggestedSalesManager,
    SuggestedProductManager,
    EmailRecipient,
} from '../../types/email';
import type { InventoryItem, InventoryBuyer, SimilarProductBuyers, FlaggedItem } from '../../types/inventory';
import { buildSlowMoverTemplate, buildDestroyListTemplate } from './emailTemplates';

type EmailContext =
    | { type: 'SlowNoMoverItem'; item: InventoryItem; buyers: InventoryBuyer[]; similarProducts: SimilarProductBuyers[] }
    | { type: 'DestroyList'; items: FlaggedItem[] };

interface Props {
    isOpen: boolean;
    context: EmailContext;
    onClose: () => void;
    onSent?: () => void;
}

interface PickedRecipient {
    email: string;
    name: string;
    source: 'salesManager' | 'productManager' | 'freeSearch';
}

export function EmailComposerModal({ isOpen, context, onClose, onSent }: Props) {
    const { user } = useAuth();
    const { toast } = useToast();

    const [loadingSuggestions, setLoadingSuggestions] = useState(true);
    const [suggestions, setSuggestions] = useState<SuggestRecipientsResponse | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [to, setTo] = useState<PickedRecipient[]>([]);
    const [cc, setCc] = useState<PickedRecipient[]>([]);

    const [subject, setSubject] = useState('');
    const [intro, setIntro] = useState('');

    const [search, setSearch] = useState('');
    const [searchTarget, setSearchTarget] = useState<'to' | 'cc'>('to');

    const [sending, setSending] = useState(false);

    const senderEmail = user?.email ?? '';

    // Build template once we have the context
    const template = useMemo(() => {
        if (!senderEmail) return null;
        if (context.type === 'SlowNoMoverItem') {
            return buildSlowMoverTemplate(context.item, context.buyers, context.similarProducts, senderEmail);
        }
        return buildDestroyListTemplate(context.items, senderEmail);
    }, [context, senderEmail]);

    // Initialize subject + intro from template
    useEffect(() => {
        if (template && subject === '' && intro === '') {
            setSubject(template.subject);
            setIntro(template.plainIntro);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template]);

    // Derive itemIds for suggest-recipients
    const itemIds = useMemo(() => {
        if (context.type === 'SlowNoMoverItem') return [context.item.ItemID];
        return context.items.map(i => i.ItemID);
    }, [context]);

    // Load suggestions on open
    useEffect(() => {
        if (!isOpen) return;
        setLoadingSuggestions(true);
        setLoadError(null);
        emailApi.suggestRecipients(itemIds)
            .then(setSuggestions)
            .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : 'Failed to load suggestions'))
            .finally(() => setLoadingSuggestions(false));
    }, [isOpen, itemIds]);

    const isPicked = useCallback((list: PickedRecipient[], email: string) => {
        const lower = email.toLowerCase();
        return list.some(r => r.email.toLowerCase() === lower);
    }, []);

    const toggleRecipient = useCallback((
        target: 'to' | 'cc',
        r: SuggestedEmployee,
        source: PickedRecipient['source'],
    ) => {
        const setter = target === 'to' ? setTo : setCc;
        setter(prev => {
            const lower = r.email.toLowerCase();
            if (prev.some(x => x.email.toLowerCase() === lower)) {
                return prev.filter(x => x.email.toLowerCase() !== lower);
            }
            return [...prev, { email: r.email, name: r.fullName, source }];
        });
    }, []);

    const removeRecipient = useCallback((target: 'to' | 'cc', email: string) => {
        const setter = target === 'to' ? setTo : setCc;
        setter(prev => prev.filter(r => r.email.toLowerCase() !== email.toLowerCase()));
    }, []);

    // Filter free-search results
    const searchResults = useMemo(() => {
        if (!suggestions || !search || search.trim().length < 2) return [];
        const q = search.trim().toLowerCase();
        return suggestions.allActiveEmployees
            .filter(e =>
                (e.fullName?.toLowerCase().includes(q) ?? false) ||
                (e.email?.toLowerCase().includes(q) ?? false) ||
                (e.department?.toLowerCase().includes(q) ?? false) ||
                (e.jobTitle?.toLowerCase().includes(q) ?? false)
            )
            .slice(0, 20);
    }, [suggestions, search]);

    // Build body-only HTML for the in-page preview (no <html>/<body> wrapper).
    // The full wrapped document is generated only at send time in handleSend.
    const previewBody = useMemo(() => template?.buildBodyContent(intro) ?? '', [template, intro]);

    const handleSend = useCallback(async () => {
        if (!template) return;
        if (to.length === 0) {
            toast('Please select at least one TO recipient', 'error');
            return;
        }
        if (!subject.trim()) {
            toast('Subject is required', 'error');
            return;
        }

        setSending(true);
        try {
            const htmlBody = template.buildHtmlBody(intro);
            const contextKey = context.type === 'SlowNoMoverItem' ? context.item.ItemID : null;
            const contextPayload = context.type === 'SlowNoMoverItem'
                ? { itemId: context.item.ItemID, productName: context.item.ProductName, companyCode: context.item.CompanyCode }
                : { itemIds: context.items.map(i => i.ItemID), count: context.items.length, totalValue: context.items.reduce((s, i) => s + (i.StockValueEUR ?? 0), 0) };

            const result = await emailApi.send({
                to: to.map<EmailRecipient>(r => ({ email: r.email, name: r.name })),
                cc: cc.map<EmailRecipient>(r => ({ email: r.email, name: r.name })),
                subject,
                htmlBody,
                plainBodyPreview: intro,
                contextType: context.type,
                contextKey,
                contextPayload,
            });

            toast(`Email sent (log #${result.emailLogId ?? '?'})`, 'success');
            onSent?.();
            onClose();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to send email', 'error');
        } finally {
            setSending(false);
        }
    }, [template, to, cc, subject, intro, context, toast, onSent, onClose]);

    if (!isOpen) return null;

    const headerTitle = context.type === 'SlowNoMoverItem'
        ? `Send Email — ${context.item.ProductName}`
        : `Send Email — Destroy list (${context.items.length} items)`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-blue-50 rounded-t-lg">
                    <div className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-blue-600" />
                        <h2 className="text-lg font-semibold text-gray-900">{headerTitle}</h2>
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 hover:bg-blue-100" aria-label="Close">
                        <X className="h-5 w-5 text-gray-600" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex">
                    {/* Left: compose */}
                    <div className="w-1/2 border-r flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                            {loadError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                                    {loadError}
                                </div>
                            )}

                            {/* Free search (first, so users can start adding recipients immediately) */}
                            <div className="border border-gray-200 rounded-md p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Search className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Search employees</span>
                                    <div className="ml-auto flex text-xs rounded overflow-hidden border">
                                        <button
                                            type="button"
                                            onClick={() => setSearchTarget('to')}
                                            className={`px-2 py-0.5 ${searchTarget === 'to' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                                        >
                                            Add to TO
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSearchTarget('cc')}
                                            className={`px-2 py-0.5 ${searchTarget === 'cc' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                                        >
                                            Add to CC
                                        </button>
                                    </div>
                                </div>
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Name, email, department..."
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                {searchResults.length > 0 && (
                                    <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded">
                                        {searchResults.map(e => {
                                            const list = searchTarget === 'to' ? to : cc;
                                            const picked = isPicked(list, e.email);
                                            return (
                                                <button
                                                    type="button"
                                                    key={e.staffId}
                                                    onClick={() => toggleRecipient(searchTarget, e, 'freeSearch')}
                                                    className={`w-full text-left px-2 py-1.5 text-sm border-b last:border-b-0 hover:bg-blue-50 ${picked ? 'bg-blue-50' : ''}`}
                                                >
                                                    <div className="font-medium text-gray-900">{e.fullName}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {e.email}{e.jobTitle ? ` · ${e.jobTitle}` : ''}{e.department ? ` · ${e.department}` : ''}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* TO pickers */}
                            <RecipientSection
                                label="To"
                                picked={to}
                                onRemove={email => removeRecipient('to', email)}
                            />

                            {/* CC pickers */}
                            <RecipientSection
                                label="Cc"
                                picked={cc}
                                fixedSelf={senderEmail}
                                onRemove={email => removeRecipient('cc', email)}
                            />

                            {/* Suggested */}
                            <SuggestionsPanel
                                loading={loadingSuggestions}
                                suggestions={suggestions}
                                toPicked={to}
                                ccPicked={cc}
                                onToggleTo={(r, source) => toggleRecipient('to', r, source)}
                                onToggleCc={(r, source) => toggleRecipient('cc', r, source)}
                            />

                            {/* Subject */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>

                            {/* Intro text (editable) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Message
                                    <span className="ml-2 text-xs text-gray-500 font-normal">
                                        (item table and footer are added automatically)
                                    </span>
                                </label>
                                <textarea
                                    value={intro}
                                    onChange={e => setIntro(e.target.value)}
                                    rows={10}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right: preview */}
                    <div className="w-1/2 flex flex-col overflow-hidden bg-gray-50">
                        <div className="px-4 py-2 border-b bg-white">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Preview</div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            <div
                                className="bg-white rounded border border-gray-200 p-4 email-preview"
                                style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '14px', color: '#1f2937', lineHeight: 1.5 }}
                                // Safe: previewBody is generated by our own emailTemplates, and all user input
                                // (intro text, item names, comments) is escaped via escapeHtml() before insertion.
                                dangerouslySetInnerHTML={{ __html: previewBody }}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50 rounded-b-lg">
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                        <Info className="h-3.5 w-3.5" />
                        Sending as <span className="font-medium text-gray-700 ml-1">{senderEmail}</span> · You will always be on CC.
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            disabled={sending}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending || to.length === 0 || !subject.trim()}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            {sending ? 'Sending...' : 'Send'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- Sub-components ---

function RecipientSection({
    label,
    picked,
    fixedSelf,
    onRemove,
}: {
    label: string;
    picked: PickedRecipient[];
    fixedSelf?: string;
    onRemove: (email: string) => void;
}) {
    return (
        <div>
            <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
            <div className="flex flex-wrap gap-1.5 min-h-[32px] px-2 py-1.5 border border-gray-200 rounded bg-white">
                {fixedSelf && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded" title="Always on CC">
                        {fixedSelf} <span className="text-gray-400">(you)</span>
                    </span>
                )}
                {picked.map(r => (
                    <span
                        key={r.email}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded"
                    >
                        {r.name || r.email}
                        <button
                            type="button"
                            onClick={() => onRemove(r.email)}
                            className="hover:bg-blue-200 rounded"
                            aria-label={`Remove ${r.email}`}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                {picked.length === 0 && !fixedSelf && (
                    <span className="text-xs text-gray-400 py-1">No recipients selected</span>
                )}
            </div>
        </div>
    );
}

function SuggestionsPanel({
    loading,
    suggestions,
    toPicked,
    ccPicked,
    onToggleTo,
    onToggleCc,
}: {
    loading: boolean;
    suggestions: SuggestRecipientsResponse | null;
    toPicked: PickedRecipient[];
    ccPicked: PickedRecipient[];
    onToggleTo: (r: SuggestedEmployee, source: PickedRecipient['source']) => void;
    onToggleCc: (r: SuggestedEmployee, source: PickedRecipient['source']) => void;
}) {
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading suggestions...
            </div>
        );
    }
    if (!suggestions) return null;

    return (
        <div className="space-y-3">
            <SuggestionGroup
                title="Sales Managers (past sellers, still active)"
                items={suggestions.suggestedSalesManagers}
                toPicked={toPicked}
                ccPicked={ccPicked}
                onToggleTo={r => onToggleTo(r, 'salesManager')}
                onToggleCc={r => onToggleCc(r, 'salesManager')}
                emptyText="No active sales managers found for these items."
            />
            <SuggestionGroup
                title="Product Managers"
                items={suggestions.suggestedProductManagers}
                toPicked={toPicked}
                ccPicked={ccPicked}
                onToggleTo={r => onToggleTo(r, 'productManager')}
                onToggleCc={r => onToggleCc(r, 'productManager')}
                emptyText="No product managers assigned for the supplier(s) of these items."
            />
        </div>
    );
}

function SuggestionGroup<T extends SuggestedSalesManager | SuggestedProductManager>({
    title,
    items,
    toPicked,
    ccPicked,
    onToggleTo,
    onToggleCc,
    emptyText,
}: {
    title: string;
    items: T[];
    toPicked: PickedRecipient[];
    ccPicked: PickedRecipient[];
    onToggleTo: (r: T) => void;
    onToggleCc: (r: T) => void;
    emptyText: string;
}) {
    return (
        <div className="border border-gray-200 rounded-md">
            <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-700">{title}</div>
            <div className="max-h-48 overflow-y-auto">
                {items.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">{emptyText}</div>
                ) : (
                    items.map(r => {
                        const inTo = toPicked.some(x => x.email.toLowerCase() === r.email.toLowerCase());
                        const inCc = ccPicked.some(x => x.email.toLowerCase() === r.email.toLowerCase());
                        return (
                            <div
                                key={r.staffId}
                                className="px-3 py-1.5 border-b last:border-b-0 text-sm hover:bg-gray-50"
                                title={r.reason}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-gray-900 truncate">{r.fullName}</div>
                                        <div className="text-xs text-gray-500 truncate">{r.email}</div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => onToggleTo(r)}
                                            className={`px-2 py-0.5 text-xs rounded border ${inTo ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                        >
                                            TO
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onToggleCc(r)}
                                            className={`px-2 py-0.5 text-xs rounded border ${inCc ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                        >
                                            CC
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
